(ns frontend.view.entity-viewers.modules.liabilities
  (:require
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.registry :as registry]
   [frontend.actions.entity :as entity-actions]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.utils :as fu]
   [frontend.view.entity :as entity-view]
   [frontend.view.modals.delete-confirmation.view :as delete-confirmation]
   [frontend.view.modals.entity-editor.view :as entity-editor]
   [frontend.view.modals.error-message.view :as error-message]
   [frontend.view.modals.events :as modals]
   [oops.core :as oops]
   [reagent.core :as r]
   [ui.components.grid.view :refer [ag-grid-enterprise theme-quartz]]
   [ui.util :as uu]))

(defn liability-keys
  [liability-id state]
  (let [current (session/get-entity liability-id)
        new @state
        schema (registry/get-schema @session/tenant-schema :entity.type/liability)]
    (->> schema
         (filterv (fn [[a _]]
                    (f-permissions/field-write-access?
                     a
                     :entity.type/liability
                     nil)))
         (mapv first)
         ;; note: might require more comprehensive logic later on:
         (filterv (fn [a]
                    (= (namespace a)
                       "liability")))
         (remove (fn [a]
                   (let [same? (= (get current a)
                                  (get new a))]
                     same?))))))

(defn open-editor
  [{:keys [read-only?] :as _props} liability-id]
  (let [liability (session/get-entity liability-id)
        state (r/atom liability)]
    (modals/open-modal!
     entity-editor/modal-key
     {:entity-id               liability-id
      :prevent-backend-update? true
      :entity-state            state
      :mandatory-fields        [:liability/description]
      :read-only?              read-only?
      :on-ok!
      (fn [liability-id callback!]
        (entity-actions/*->entity-update!
         liability-id
         (select-keys
          @state
          (liability-keys liability-id state))
         (fn [_] (callback!))
         (fn [{:keys [message] :as e}]
           (println "Error updating liability: " e)
           (modals/open-modal!
            error-message/modal-key
            {:title          (loc/tr :attention)
             :ok-button-text (loc/tr :ok)
             :body-content
             (or message
                 (loc/tr :unknown-error-persists))}))))})))

(defn edit-liability
  [props js-args]
  (let [liability-id (oops/oget js-args "data.id")]
    (open-editor props liability-id)))

(defn column-def
  [props attribute]
  {:field         attribute
   :headerName    (loc/tr attribute)
   :headerTooltip (loc/tr attribute)
   :onCellClicked (partial edit-liability props)
   :cellClass     "cursor-pointer"
   :flex          2
   :minWidth      (case attribute
                    :liability/description 170
                    :liability/agreement-reference 175
                    :liability/cur-max-amount 120
                    :liability/datetime-expiry-date 110
                    :liability/covered-by-insurance 180
                    :liability/cur-max-amount-covered 180
                    150)})

(defn delete-liability!
  [entity-id]
  (modals/open-modal!
   delete-confirmation/modal-key
   (merge
    {:body
     (loc/tr
      :delete-confirmation-body-text
      [(loc/tr :entity.type/liability)
       (entity/resolve-entity-name entity-id
                                   session/get-entity
                                   session/reverse-lookup)])
     :on-delete!
     (fn [_]
       (entity-actions/*->disable!
        {:db/id entity-id}
        identity
        ;;TODO error handling:
        identity))})))

(defn action-column-cell
  [{:keys [_entity]} js-args]
  (let [liability-id (oops/oget js-args "data.id")]
    (r/as-element
     [:div
      {:class (uu/cm "flex flex-col justify-center items-center h-full w-full")}
      [:button
       {:on-click
        (partial delete-liability! liability-id)}
       [:i
        {:class (uu/cm "fal fa-trash-alt")}]]])))

(defn column-defs
  [{:keys [show-delete?] :as props}]
  (let [data-columns (->> [:liability/description
                           :liability/agreement-reference
                           :liability/cur-max-amount
                           :liability/datetime-expiry-date
                           :liability/covered-by-insurance
                           :liability/cur-max-amount-covered
                           :liability/notes]
                          (mapv (partial column-def props)))]
    (if-not show-delete?
      data-columns
      (conj data-columns
            {:headerName   ""
             ;;:pinned :right
             :flex         2
             :resizable    false
             :sortable     false
             :width        30
             :cellRenderer (partial action-column-cell props)
             :cellStyle    {:padding "0px"}
             :cellClass    (uu/cm "border-r-transparent focus:border-y-transparent"
                                  "focus:border-l-[var(--ag-cell-horizontal-border)]"
                                  "focus:outline-none"
                                  "focus-within:border-y-transparent"
                                  "focus-within:border-l-[var(--ag-cell-horizontal-border)]"
                                  "focus-within:outline-none")}))))

(defn format-value
  [entity attribute]
  (let [tenant-schema @session/tenant-schema
        tenant @session/tenant
        fs (registry/get-field-schema tenant-schema attribute)]
    (fu/display-data tenant-schema tenant attribute fs entity
                     :show-currency? true)))

(defn liability-row
  [{:liability/keys [cur-max-amount cur-max-amount--sym
                     cur-max-amount-covered cur-max-amount-covered--sym
                     datetime-expiry-date covered-by-insurance] :as liability}]
  (cond-> liability
    (and cur-max-amount cur-max-amount--sym)
    (assoc :liability/cur-max-amount
           (format-value liability :liability/cur-max-amount))
    datetime-expiry-date
    (assoc :liability/datetime-expiry-date
           (format-value liability :liability/datetime-expiry-date))
    covered-by-insurance
    (assoc :liability/covered-by-insurance
           (format-value liability :liability/covered-by-insurance))
    (and cur-max-amount-covered cur-max-amount-covered--sym)
    (assoc :liability/cur-max-amount-covered
           (format-value liability :liability/cur-max-amount-covered))))

(defn row-data
  [liabilities]
  (->> liabilities
       (mapv liability-row)))

(defn module-content
  [{:keys [entity liabilities show-delete? read-only?]}]
  [:div
   [ag-grid-enterprise
    {:theme               theme-quartz
     :rowData             (row-data liabilities)
     :columnDefs          (column-defs {:entity       entity
                                        :read-only?   read-only?
                                        :show-delete? show-delete?})
     :domLayout           :autoHeight
     :suppressContextMenu true
     :defaultColDef       {:sortable                 false
                           :filter                   false
                           :resizable                true
                           :editable                 false
                           :suppressHeaderMenuButton true}}]])

(defn open-create-liability-modal
  [{:keys [db/id] :as _entity}]
  (entity-view/open-creator!
   :entity.type/liability
   {:start-value {:common/containing-entity id}
    :on-ok!
    (fn [{:keys [close-cb!]}]
      (close-cb!))}))

(defn add-new-liability-button
  [{:keys [entity show-create?]}]
  [:div
   (when show-create?
     [fu/tooltip-overlay
      {:id (str "add-liability-" (:db/id entity))}
      (loc/tr :add-new-liability)
      [:i
       {:class-name (fu/cs "fal clickable fa-plus-circle")
        :on-click   (fn [_]
                      (open-create-liability-modal entity))}]])])

(defn module
  [{:keys [save-module-collapsed! module-collapsed-path module-id
           entity-id header show-create? show-delete? read-only?]}]
  (let [entity @(r/track session/get-entity entity-id)
        liabilities (->> entity
                         :common/liabilities
                         (keep session/get-entity))]
    [{:module-id              module-id
      :header                 (or header (loc/tr :liabilities))
      :header-tools           [add-new-liability-button {:entity       entity
                                                         :show-create? show-create?}]
      :collapsed?             @(r/track session/get-view-config module-collapsed-path)
      :start-collapsed?       (empty? liabilities)
      :save-module-collapsed! (r/partial save-module-collapsed! module-collapsed-path)
      :content                [:div
                               (if (empty? liabilities)
                                 [:h2.mt-3
                                  (if (session/is-read-only-user?)
                                    (loc/tr :no-liabilities-read-only)
                                    (loc/tr :no-liabilities))]
                                 [:div.table__wrapper.table-responsive.mt-3
                                  [module-content
                                   {:entity       entity
                                    :liabilities  liabilities
                                    :read-only?   read-only?
                                    :show-delete? show-delete?}]])]}]))
